import "./utility-tools.css";
import { createOperationResult } from "./operation-result.js";
import {
  compressedPngName,
  formatBytes,
  normalizePngColorCount,
  pngCompressionSummary,
  validatePngFile
} from "./compress-png-core.js";

const root = document.querySelector("[data-compress-png]");

if (root) {
  const ids = [
    "pngStatus", "pngUpload", "pngDropZone", "choosePng", "pngFileInput", "pngWorkspace",
    "pngPreview", "pngFileName", "pngDetails", "replacePng", "pngColors", "compressPng",
    "pngProgress"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { file: null, previewUrl: null, busy: false, worker: null };
  const result = createOperationResult(root, elements.pngWorkspace);

  function status(message = "", tone = "error") {
    elements.pngStatus.textContent = message;
    elements.pngStatus.hidden = !message;
    elements.pngStatus.dataset.tone = tone;
  }

  function controls() {
    elements.choosePng.disabled = state.busy;
    elements.replacePng.disabled = state.busy;
    elements.pngColors.disabled = state.busy;
    elements.compressPng.disabled = state.busy || !state.file;
    elements.pngProgress.hidden = !state.busy;
  }

  async function loadFile(file) {
    if (!file || state.busy) return;
    state.busy = true;
    status("Checking PNG…", "progress");
    controls();
    try {
      const header = new Uint8Array(await file.slice(0, 32).arrayBuffer());
      const dimensions = validatePngFile(file, header);
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      state.file = file;
      state.previewUrl = URL.createObjectURL(file);
      elements.pngPreview.src = state.previewUrl;
      elements.pngPreview.alt = `Preview of ${file.name || "selected PNG"}`;
      elements.pngFileName.textContent = file.name || "image.png";
      elements.pngDetails.textContent = `${dimensions.width} × ${dimensions.height} px · ${formatBytes(file.size)}`;
      elements.pngUpload.hidden = true;
      elements.pngWorkspace.hidden = false;
      status();
    } catch (error) {
      status(error?.message || "The PNG could not be opened.");
    } finally {
      state.busy = false;
      elements.pngFileInput.value = "";
      controls();
    }
  }

  function runWorker() {
    return new Promise(async (resolve, reject) => {
      const worker = new Worker("/dist/compress-png-worker.js", { type: "module" });
      state.worker = worker;
      const id = crypto.randomUUID();
      const finish = callback => {
        worker.terminate();
        if (state.worker === worker) state.worker = null;
        callback();
      };
      worker.addEventListener("message", event => {
        if (event.data?.id !== id) return;
        event.data.ok ? finish(() => resolve(event.data)) : finish(() => reject(new Error(event.data.error)));
      });
      worker.addEventListener("error", () => finish(() => reject(new Error("The PNG compressor could not start."))), { once: true });
      try {
        const buffer = await state.file.arrayBuffer();
        worker.postMessage({ id, buffer, colorCount: normalizePngColorCount(elements.pngColors.value) }, [buffer]);
      } catch (error) {
        finish(() => reject(error));
      }
    });
  }

  async function compress() {
    if (!state.file || state.busy) return;
    state.busy = true;
    status("Compressing PNG…", "progress");
    controls();
    try {
      const output = await runWorker();
      const summary = pngCompressionSummary(state.file.size, output.buffer.byteLength);
      const animation = output.frameCount > 1 ? `${output.frameCount} animation frames preserved` : "Static PNG";
      if (output.buffer.byteLength >= state.file.size) {
        result.show({
          title: "Already optimized",
          message: "These settings would not make this PNG smaller.",
          details: [summary, animation]
        });
      } else {
        result.show({
          blob: new Blob([output.buffer], { type: "image/png" }),
          fileName: compressedPngName(state.file.name),
          message: "Your PNG was compressed successfully.",
          details: [summary, `${output.width} × ${output.height} px`, animation]
        });
      }
    } catch (error) {
      status(error?.message || "The PNG could not be compressed.");
    } finally {
      state.busy = false;
      controls();
    }
  }

  elements.choosePng.addEventListener("click", event => { event.stopPropagation(); elements.pngFileInput.click(); });
  elements.replacePng.addEventListener("click", () => elements.pngFileInput.click());
  elements.pngFileInput.addEventListener("change", () => loadFile(elements.pngFileInput.files?.[0]));
  elements.compressPng.addEventListener("click", compress);
  elements.pngDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.pngFileInput.click(); });
  elements.pngDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.pngFileInput.click(); }
  });
  elements.pngDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.pngDropZone.classList.add("is-dragging"); });
  elements.pngDropZone.addEventListener("dragleave", () => elements.pngDropZone.classList.remove("is-dragging"));
  elements.pngDropZone.addEventListener("drop", event => {
    event.preventDefault();
    elements.pngDropZone.classList.remove("is-dragging");
    loadFile(event.dataTransfer?.files?.[0]);
  });
  window.addEventListener("pagehide", () => {
    state.worker?.terminate();
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    result.dispose();
  }, { once: true });
  controls();
}
